import { Dialog, DialogTitle, DialogContent, DialogActions, FormControl, InputLabel, MenuItem, Select } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { ModalLoading } from "../../../../../../common/components/modal_loading";
import FormContainer from "../../../../../../common/components/form_container";
import PrimaryButton from "../../../../../../common/components/primary_button";
import { MaskedInput } from "../../../../../../common/components/masked_inputs/masked_input";
import { EmpreendimentoBlocoCompletoTable } from "../../../../domain/models/empreendimentoBlocoTable";

/**
 * CadastroUnidadesBlocoModal
 */

type Modo = 'andares' | 'unidades'

type Props = {
  open: boolean,
  saving: boolean,
  bloco: EmpreendimentoBlocoCompletoTable | undefined,
  onSubmit: (andaresQuantidade: number, unidadesQuantidade: number, idAndar: number | null) => Promise<void>,
  onClose: () => void,
};

// Converte o valor da máscara ("0_", "__") em número; vazio vira 0.
const parseQuantidade = (value: string | null | undefined) => {
  const parsed = Number((value ?? '').replace(/_/g, ''));
  return isNaN(parsed) ? 0 : parsed;
};

export const CadastroUnidadesBlocoModal = ({
  open,
  saving,
  bloco,
  onSubmit,
  onClose,
}: Props) => {
  const isApartamento = (bloco?.listIncorporacaoAndar.length ?? 0) > 0

  const [modo, setModo] = useState<Modo>('andares');
  const [idAndar, setIdAndar] = useState<number>(0);
  const [andaresQuantidade, setAndaresQuantidade] = useState<string>();
  const [unidadesQuantidade, setUnidadesQuantidade] = useState<string>();

  useEffect(() => {
    if (open) {
      setModo(isApartamento ? 'andares' : 'unidades');
      setIdAndar(0);
      setAndaresQuantidade("__");
      setUnidadesQuantidade("__");
    }
  }, [open, isApartamento]);

  const handleClose = useCallback(() => {
    if (!saving) onClose();
  }, [saving, onClose]);

  const handleSubmit = useCallback(async () => {
    if (saving) return;
    const andares = isApartamento && modo === 'andares' ? parseQuantidade(andaresQuantidade) : 0;
    const andarSelecionado = isApartamento && modo === 'unidades' && idAndar > 0 ? idAndar : null;
    await onSubmit(andares, parseQuantidade(unidadesQuantidade), andarSelecionado);
  }, [saving, isApartamento, modo, andaresQuantidade, unidadesQuantidade, idAndar, onSubmit]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      sx={{
        "& .MuiDialog-container": {
          "& .MuiPaper-root": {
            width: "100%",
            maxWidth: "45rem",
          },
        },
      }}>
      <DialogTitle>
        {isApartamento ? 'Novos andares / unidades' : 'Novas unidades'} - {bloco?.nomeBloco ?? 'Bloco'}
      </DialogTitle>

      <DialogContent>
        <ModalLoading loading={saving}>
          <FormContainer title={"Informações"} arrangement="fullRow">
            <>
              {isApartamento &&
                <FormControl size="medium" fullWidth>
                  <InputLabel id="modo-cadastro-unidades">Cadastrar</InputLabel>
                  <Select
                    labelId="modo-cadastro-unidades"
                    label="Cadastrar"
                    value={modo}
                    onChange={(e) => setModo(e.target.value as Modo)}>
                    <MenuItem value="andares">Novos andares</MenuItem>
                    <MenuItem value="unidades">Unidades em andares existentes</MenuItem>
                  </Select>
                </FormControl>
              }

              {isApartamento && modo === 'andares' &&
                <>
                  <MaskedInput
                    name="andaresQuantidade"
                    label="Quantidade de novos andares"
                    mask="000"
                    maskPlaceholder="___"
                    value={andaresQuantidade ?? null}
                    onChange={val => setAndaresQuantidade(val)} />

                  <MaskedInput
                    name="unidadesQuantidade"
                    label="Quantidade de apartamentos por andar"
                    mask="00"
                    maskPlaceholder="__"
                    value={unidadesQuantidade ?? null}
                    onChange={val => setUnidadesQuantidade(val)} />
                </>
              }

              {isApartamento && modo === 'unidades' &&
                <>
                  <FormControl size="medium" fullWidth>
                    <InputLabel id="andar-cadastro-unidades">Andar</InputLabel>
                    <Select
                      labelId="andar-cadastro-unidades"
                      label="Andar"
                      value={idAndar}
                      onChange={(e) => setIdAndar(Number(e.target.value))}>
                      <MenuItem value={0}>Todos os andares</MenuItem>
                      {bloco?.listIncorporacaoAndar.map((andar) => (
                        <MenuItem key={andar.idIncorporacaoAndar} value={andar.idIncorporacaoAndar}>
                          {andar.nroAndar}° andar
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <MaskedInput
                    name="unidadesQuantidade"
                    label={idAndar > 0 ? "Quantidade de novos apartamentos" : "Quantidade de novos apartamentos por andar"}
                    mask="00"
                    maskPlaceholder="__"
                    value={unidadesQuantidade ?? null}
                    onChange={val => setUnidadesQuantidade(val)} />
                </>
              }

              {!isApartamento &&
                <MaskedInput
                  name="unidadesQuantidade"
                  label="Quantidade de novas unidades"
                  mask="0000"
                  maskPlaceholder="____"
                  value={unidadesQuantidade ?? null}
                  onChange={val => setUnidadesQuantidade(val)} />
              }
            </>
          </FormContainer>
        </ModalLoading>
      </DialogContent>

      <DialogActions>
        <div className="w-full h-full items-center justify-end flex p-4 gap-4">
          <div className="flex flex-row gap-2">
            <div className="p-2 hover:bg-hover-light-gray cursor-pointer dark:hover:bg-border-dark-gray rounded-[4px] font-semibold">
              <h1
                className="text-sm text-black dark:text-white"
                onClick={handleClose}>
                Cancelar
              </h1>
            </div>

            <div className="w-[120px]">
              {saving ? (
                <PrimaryButton
                  buttonColor="primary-green">
                  Salvando...
                </PrimaryButton>
              ) : (
                <PrimaryButton
                  buttonColor="primary-green"
                  onClick={handleSubmit}>
                  Salvar
                </PrimaryButton>
              )}
            </div>
          </div>
        </div>
      </DialogActions>
    </Dialog>
  );
}
